import { useState } from "react";
import { Loader2 } from "lucide-react";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { useUpdateQueryStatus } from "@/hooks/use-edc-queries";
import { useAuth } from "@/auth/useAuth";
import type { EdcRecord } from "@/api/types";

export function QueryInspector({ data }: { data: unknown }) {
  const query = data as EdcRecord;
  const updateStatus = useUpdateQueryStatus();
  const { can } = useAuth();
  const canEdit = can("edc_data:write");

  const [selectedStatus, setSelectedStatus] = useState(query.status ?? "open");

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground">{query.label}</h3>
        <div className="mt-2">
          <StatusBadge status={query.status} />
        </div>
      </div>

      {canEdit && (
        <div className="space-y-2 border-t border-border pt-4" data-testid="query-status-form">
          <label className="block text-xs font-medium text-muted-foreground mb-1">Update Status</label>
          <div className="flex gap-2">
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
            >
              <option value="open">Open</option>
              <option value="answered">Answered</option>
              <option value="closed">Closed</option>
            </select>
            <button
              onClick={() => updateStatus.mutate({ queryId: query.id, status: selectedStatus })}
              disabled={updateStatus.isPending || selectedStatus === query.status}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {updateStatus.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Update
            </button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <Detail label="EDC System" value={query.edcSystem} />
        <Detail label="Study" value={query.edcStudyId} />
        {query.edcSiteId && <Detail label="Site" value={query.edcSiteId} />}
        {query.edcSubjectId && <Detail label="Subject" value={query.edcSubjectId} />}
      </div>

      {query.data && Object.keys(query.data).length > 0 && (
        <div>
          <h4 className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Query Details
          </h4>
          <div className="rounded-lg border border-border divide-y divide-border">
            {Object.entries(query.data).map(([key, value]) => (
              <div key={key} className="flex justify-between gap-4 px-3 py-2">
                <span className="text-xs font-medium text-muted-foreground truncate">{key}</span>
                <span className="text-xs text-foreground text-right truncate">
                  {value === null || value === undefined ? "—" : String(value)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <p className="text-sm text-foreground">{value}</p>
    </div>
  );
}
